import type React from "react"
import Link from "next/link"

// ============================================================
// BOOTH — shared renderer for the legal pages (/terms, /privacy, /refunds)
// ------------------------------------------------------------
// These pages had their entire text hardcoded in English inside the JSX, which
// meant /es/terms served English — the one place a user is agreeing to
// something. The copy now lives in messages/pages/<ns>.{en,es}.json and every
// page is this component plus a dictionary.
//
// MARKUP IN A TRANSLATION STRING. Legal copy needs bold leads and the odd
// mailto, and a translator must be able to move them inside the sentence. Two
// markers are supported, deliberately only two:
//
//     **bold**                     -> <strong>
//     [label](mailto:… | /path)    -> <a> or next/link
//
// Anything else is rendered literally. No HTML is interpreted — a dictionary is
// not a template, and `dangerouslySetInnerHTML` over translated strings is how
// a copy edit becomes an XSS.
// ============================================================

export interface LegalSection {
  /** Anchor id. Keep it stable — these are linked to from elsewhere. */
  id: string
  heading: string
  /** Paragraphs before the bullets. */
  body?: string[]
  bullets?: string[]
  /** Numbered rather than bulleted — "how to request a refund" is a sequence. */
  ordered?: boolean
  /** Paragraphs after the bullets (§9 reads intro, list, then more prose). */
  after?: string[]
}

export interface LegalDocument {
  title: string
  lastUpdated: string
  /** Shown under the date where a translation is not the governing version. */
  translationNote?: string
  sections: LegalSection[]
  /** Small print under a rule at the end — e.g. "the Terms control". */
  footnote?: string
}

const LINK = /\[([^\]]+)\]\(([^)]+)\)/
const BOLD = /\*\*([^*]+)\*\*/

/** Render one string's `**bold**` and `[label](href)` markers. */
export function renderLegalText(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  let rest = text
  let n = 0

  while (rest.length > 0) {
    const bold = BOLD.exec(rest)
    const link = LINK.exec(rest)
    // Whichever marker comes first in what's left; neither means we're done.
    const next =
      bold && link ? (bold.index <= link.index ? "bold" : "link") : bold ? "bold" : link ? "link" : null

    if (next === null) {
      out.push(rest)
      break
    }

    const m = (next === "bold" ? bold : link) as RegExpExecArray
    if (m.index > 0) out.push(rest.slice(0, m.index))

    if (next === "bold") {
      out.push(
        <strong key={`${keyPrefix}-b${n}`} className="text-foreground font-medium">
          {m[1]}
        </strong>,
      )
    } else {
      const [, label, href] = m
      const className = "text-foreground underline"
      out.push(
        href.startsWith("/") ? (
          <Link key={`${keyPrefix}-l${n}`} href={href} className={className}>
            {label}
          </Link>
        ) : (
          <a key={`${keyPrefix}-l${n}`} href={href} className={className}>
            {label}
          </a>
        ),
      )
    }

    rest = rest.slice(m.index + m[0].length)
    n += 1
  }

  return out
}

export function LegalDocumentBody({
  doc,
  compact = false,
}: {
  doc: LegalDocument
  /** Tighter headings and spacing, as /refunds has always rendered. */
  compact?: boolean
}) {
  return (
    <>
      <h1 className="text-3xl font-bold text-foreground mb-2">{doc.title}</h1>
      <p className="text-sm text-muted-foreground mb-1">{doc.lastUpdated}</p>
      {doc.translationNote ? (
        <p className="text-xs text-muted-foreground mb-8 italic">{doc.translationNote}</p>
      ) : (
        <div className="mb-8" />
      )}

      <div className={`prose prose-zinc dark:prose-invert max-w-none ${compact ? "space-y-6" : "space-y-8"}`}>
        {doc.sections.map((s) => (
          <section key={s.id} id={s.id}>
            <h2
              className={`font-semibold text-foreground ${compact ? "text-lg mb-2" : "text-xl mb-3"}`}
            >
              {s.heading}
            </h2>

            {s.body?.map((p, i) => (
              <p
                key={`${s.id}-p${i}`}
                className={`text-muted-foreground leading-relaxed${
                  i < (s.body?.length ?? 0) - 1 || s.bullets?.length ? " mb-3" : ""
                }`}
              >
                {renderLegalText(p, `${s.id}-p${i}`)}
              </p>
            ))}

            {s.bullets?.length ? (
              s.ordered ? (
                <ol
                  className={`list-decimal list-inside text-muted-foreground space-y-2${
                    s.after?.length ? " mb-3" : ""
                  }`}
                >
                  {s.bullets.map((li, i) => (
                    <li key={`${s.id}-li${i}`}>{renderLegalText(li, `${s.id}-li${i}`)}</li>
                  ))}
                </ol>
              ) : (
                <ul
                  className={`list-disc list-inside text-muted-foreground space-y-2${
                    s.after?.length ? " mb-3" : ""
                  }`}
                >
                  {s.bullets.map((li, i) => (
                    <li key={`${s.id}-li${i}`}>{renderLegalText(li, `${s.id}-li${i}`)}</li>
                  ))}
                </ul>
              )
            ) : null}

            {s.after?.map((p, i) => (
              <p
                key={`${s.id}-a${i}`}
                className={`text-muted-foreground leading-relaxed${
                  i < (s.after?.length ?? 0) - 1 ? " mb-3" : ""
                }`}
              >
                {renderLegalText(p, `${s.id}-a${i}`)}
              </p>
            ))}
          </section>
        ))}

        {doc.footnote ? (
          <section>
            <p className="text-sm text-muted-foreground leading-relaxed pt-4 border-t border-border">
              {renderLegalText(doc.footnote, "footnote")}
            </p>
          </section>
        ) : null}
      </div>
    </>
  )
}
